import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { 
  Select, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { CurrencySelectContent } from '../common/CurrencySelectContent';
import { Globe2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Currency Selector for Multi-Currency Report Presentation
 * 
 * Allows users to view financial reports in different currencies.
 * Fetches live exchange rates for conversion.
 */
const CurrencySelector = ({ 
  value, 
  onChange, 
  baseCurrency = 'NGN',
  showRate = true,
  className = '' 
}) => {
  const [currencies, setCurrencies] = useState([]);
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    if (value && value !== baseCurrency) {
      fetchExchangeRate();
    } else {
      setRate(null);
    }
  }, [value, baseCurrency]);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/ai-insights/available-currencies`);
      setCurrencies(response.data.currencies || []);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
      // Fallback currencies
      setCurrencies([
        { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
        { code: 'USD', name: 'US Dollar', symbol: '$' },
        { code: 'GBP', name: 'British Pound', symbol: '£' },
        { code: 'EUR', name: 'Euro', symbol: '€' },
      ]);
    }
  };

  const fetchExchangeRate = async () => {
    if (!value || value === baseCurrency) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fx/rates/smart/${baseCurrency}/${value}`);
      setRate(response.data);
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || code;
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Globe2 className="w-4 h-4 text-muted-foreground" />
      <Select value={value || baseCurrency} onValueChange={onChange}>
        <SelectTrigger className="w-[180px]" data-testid="currency-selector">
          <SelectValue placeholder="Select currency" />
        </SelectTrigger>
        <CurrencySelectContent pin={baseCurrency} compact testId="report-currency-content" />
      </Select>
      
      {showRate && value && value !== baseCurrency && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {loading ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : rate ? (
            <span>
              1 {baseCurrency} = {rate.rate?.toFixed(4) || '...'} {value}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
